/**
 * FullyDynamicGratuity Component
 *
 * Provides tip rate selection for users on appointments.
 * Only displays in markets where adjustable tip rates are enabled.
 *
 * Supports two contexts via the `variant` prop:
 *   - 'checkout' (default): Booking flow — "Gratuity" title, 20% pre-selected, "I'll tip in cash"
 *   - 'survey': Post-appointment survey — "Show your appreciation", no pre-selection, "I tipped in cash"
 *   - 'multiSurvey': compact per-provider section in multi-service survey
 *
 * Supports two button modes via the `mode` prop:
 *   - 'percentage' (default): 20%, 25%, 30% presets + Other
 *   - 'dollar': $5, $10, $15 presets + Other (for partial tippers in survey)
 *
 * Props:
 * @param {function} onTipRateChange - Callback when tip rate changes
 * @param {function} [onCustomInputModeChange] - Callback when custom input opens/closes
 * @param {boolean} isTipRateAdjustableMarket - Whether market supports adjustable tips
 * @param {number} baseTipAmount - Base amount for tip calculations (service price + fee)
 * @param {number} finalPrice - Final price after credits (hides component when $0 in checkout)
 * @param {string} [variant='checkout'] - Visual variant ('checkout' | 'survey' | 'multiSurvey')
 * @param {string} [mode='percentage'] - Button mode ('percentage' | 'dollar')
 * @param {number} [currentTipDollarAmount=0] - Current tip in dollars (for survey subtitle)
 * @param {boolean} [initialTipInCash=false] - Pre-populate cash checkbox from booking
 * @param {function} [onTipInCashChange] - Callback when cash checkbox toggled
 * @param {function} [onDollarAmountChange] - Callback for dollar mode selections
 */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { gsColors } from '../../styles/Variables'
import { mq, formatCurrency } from '../../helpers'
import {
  trackCheckoutTipRateSelected,
  trackCheckoutTipInCashToggled,
  trackSurveyTipRateSelected,
  trackSurveyTipInCashToggled,
  trackSurveyTipDollarAmountSelected,
} from '../../helpers/tracking'

// Percentage-rate presets — shared across all variants
const TIP_OPTIONS = [
  { rate: 0.2, label: '20%' },
  { rate: 0.25, label: '25%' },
  { rate: 0.3, label: '30%' }
]

// Dollar presets for partial tippers (0 < tipRate < 0.2) in the survey flow.
// Matches mobile app behavior exactly.
const DOLLAR_TIP_OPTIONS = [
  { amount: 5, label: '$5' },
  { amount: 10, label: '$10' },
  { amount: 15, label: '$15' }
]

// Custom tip max: 100% of base tip amount
const MAX_TIP_RATE = 1.0

// --- Variant config system ---
// Each variant encapsulates the visual/text differences for a specific context.
// This pattern mirrors the mobile TipRateSelector's getVariantConfig.
const getVariantConfig = (variant, { currentTipDollarAmount, hidePresets } = {}) => {
  switch (variant) {
    case 'survey':
      return {
        showTitle: true,
        title: 'Show your appreciation',
        // Drop "add a little more?" when presets are hidden \u2014 user already adjusted via modal
        subtitle: hidePresets
          ? `You tipped ${formatCurrency(currentTipDollarAmount ?? 0)}`
          : `You tipped ${formatCurrency(currentTipDollarAmount ?? 0)} \u2014 add a little more?`,
        tipInCashText: 'I tipped in cash',
        showBorder: false,
        centerAlign: true,
        // No default tip selection — user must actively choose
        defaultTipRate: null,
      }
    case 'multiSurvey':
      return {
        showTitle: true,   // was false — now true so subtitle renders inside the component
        title: '',         // no title text (provider photo + name serves as header above)
        // Same subtitle pattern as single-survey:
        // - Normal state: "You tipped $X — add a little more?"
        // - After adjustment (hidePresets=true): "You tipped $Y" (no CTA, presets are hidden)
        subtitle: hidePresets
          ? `You tipped ${formatCurrency(currentTipDollarAmount ?? 0)}`
          : `You tipped ${formatCurrency(currentTipDollarAmount ?? 0)} — add a little more?`,
        tipInCashText: 'I tipped in cash',
        showBorder: false,
        centerAlign: false,
        defaultTipRate: null,
      }
    case 'checkout':
    default:
      return {
        showTitle: true,
        title: 'Gratuity',
        subtitle: 'Pros always appreciate your generosity',
        tipInCashText: "I'll tip in cash",
        showBorder: true,
        centerAlign: false,
        // 20% selected by default in checkout
        defaultTipRate: 0.2,
      }
  }
}

const GratuityContainer = styled.section`
  background-color: ${gsColors.white};
  width: 100%;
  max-width: 379px;
  margin: 50px auto 0;
  border-top: 1px solid #c7c7c7;
  padding-top: 35px;
  display: flex;
  flex-direction: column;

  ${mq('max', '768px')} {
    max-width: none;
    margin: 0 0 30px;
    padding: 30px 16px;
  }

  .gratuity-header {
    width: 100%;
    margin-bottom: 16px;
  }

  .gratuity-title {
    font-family: Euclid, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #000;
    line-height: normal;
    margin: 0 0 6px;

    ${mq('max', '768px')} {
      font-size: 20px;
    }
  }

  .gratuity-description {
    font-family: Euclid, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #878787;
    line-height: normal;
    margin: 0;

    ${mq('max', '768px')} {
      font-size: 16px;
    }
  }

  .tip-button-row {
    display: flex;
    width: 100%;
    gap: 8px;
  }

  .tip-button {
    flex: 1;
    height: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    border: 1px solid #e7ddce;
    background-color: transparent;
    font-family: Euclid, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #000;
    cursor: pointer;

    &.active {
      background-color: ${gsColors.black};
      border-color: ${gsColors.black};
      color: ${gsColors.white};

      .tip-rate-label {
        color: ${gsColors.white};
      }
      .tip-amount-label {
        color: ${gsColors.white};
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    .tip-rate-label {
      font-size: 15px;
      font-weight: 700;
      line-height: 22px;
      margin: 0;
    }

    .tip-amount-label {
      font-size: 13px;
      line-height: 18px;
      color: ${gsColors.darkGrey};
      margin: 0;
    }
  }

  /* Wrapper for the underlined input + Save Tip button */
  .custom-input-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 12px;
  }

  /* Flex row for optional "$" prefix + the underlined input */
  .custom-input-wrapper {
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${gsColors.meredithGrey};
  }

  .custom-dollar-prefix {
    font-family: Euclid, sans-serif;
    font-size: 16px;
    color: #000;
    padding: 12px 0;
  }

  /* Underlined text input — border lives on the wrapper so the "$" aligns */
  .custom-tip-underline-input {
    flex: 1;
    border: none;
    background: transparent;
    font-family: Euclid, sans-serif;
    font-size: 16px;
    color: ${gsColors.black};
    padding: 12px 0;
    outline: none;
    caret-color: ${gsColors.sunsetOrange};

    &::placeholder {
      color: ${gsColors.meredithGrey};
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
    appearance: textfield;
  }

  .custom-tip-error {
    font-family: Euclid, sans-serif;
    font-size: 13px;
    color: ${gsColors.sunsetOrange};
    margin: 8px 0 0;
  }

  /* Full-width button — disabled (grey) by default, active (black) when enabled */
  .save-tip-button {
    width: 100%;
    height: 48px;
    margin-top: 30px;
    border-radius: 0;
    font-family: Euclid, sans-serif;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1.44px;
    text-transform: capitalize;

    /* Disabled state: grey border + text, no fill */
    border: 1px solid ${gsColors.meredithGrey};
    background: transparent;
    color: ${gsColors.textGrey};
    cursor: default;

    /* Active state: tertiary CTA — black border, no fill (per Figma spec) */
    &:not(:disabled) {
      border-color: ${gsColors.black};
      background: transparent;
      color: ${gsColors.black};
      cursor: pointer;
    }
  }

  .tip-in-cash-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    font-family: Euclid, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: ${gsColors.black};
    cursor: pointer;
  }

  .tip-in-cash-checkbox {
    width: 24px;
    height: 24px;
    border: 1px solid #e7ddce;
    border-radius: 3px;
    cursor: pointer;
    accent-color: ${gsColors.black};
  }

  /* "Adjust tip" — underlined text link below tip buttons (survey only).
     Positioned between the tip button row and the custom input section. */
  .adjust-tip-link {
    all: unset;
    font-family: Euclid, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: ${gsColors.black};
    text-decoration: underline;
    cursor: pointer;
    margin-top: 16px;
    align-self: center;

    &:hover {
      opacity: 0.7;
    }

    ${mq('max', '768px')} {
      margin-top: 12px;
    }
  }

  /* --- Survey variant overrides ---
     Applied via data-variant="survey". Checkout is unaffected
     because it gets data-variant="checkout" (no matching rules).
     Spacing values derived from Figma:
       Desktop: title 18px → 10px gap → subtitle 13px → 25px gap → buttons 56px
       Mobile:  title 13px → 5px gap  → subtitle 13px → 20px gap → buttons 40px */
  &[data-variant="survey"] {
    border-top: none;
    margin: 0 auto;
    padding-top: 0;
    max-width: 400px;
    align-items: center;

    ${mq('max', '768px')} {
      max-width: none;
      margin: 0;
      padding: 0 25px;
    }

    .gratuity-header {
      text-align: center;
      /* Space between subtitle and buttons */
      margin-bottom: 25px;

      ${mq('max', '768px')} {
        margin-bottom: 20px;
      }
    }

    .gratuity-title {
      font-size: 18px;
      font-weight: 600;
      line-height: 28px;
      /* Space between title and subtitle */
      margin-bottom: 2px;

      ${mq('max', '768px')} {
        font-size: 13px;
      }
    }

    .gratuity-description {
      color: #545454;
      font-size: 13px;
      line-height: 18px;
    }

    .tip-button-row {
      /* Figma: 12px gap between buttons */
      gap: 12px;
    }

    .tip-button {
      /* Desktop: 91px × 56px per Figma */
      height: 56px;

      ${mq('max', '768px')} {
        /* Mobile: 80px × 40px per Figma */
        height: 40px;
      }

      .tip-rate-label {
        /* Desktop: 16px. Mobile: 13px */
        font-size: 16px;

        ${mq('max', '768px')} {
          font-size: 13px;
          line-height: 1.1;
        }
      }

      .tip-amount-label {
        /* Desktop: 16px. Mobile: 13px */
        font-size: 16px;

        ${mq('max', '768px')} {
          font-size: 13px;
          line-height: 1.1;
        }
      }
    }

    /* Cash checkbox: left-aligned within the gratuity selector */
    .tip-in-cash-label {
      align-self: flex-start;
    }
  }

  /* --- MultiSurvey variant overrides --- */
  &[data-variant="multiSurvey"] {
    border-top: none;
    margin: 0;
    padding-top: 0;
    max-width: none;
    align-items: flex-start;
    gap: 12px;

    ${mq('max', '768px')} {
      margin: 0;
      padding: 0;
    }

    /* Subtitle (gratuity-description) — matches the .current-tip-label styling
       that previously lived in appointment-survey styles.js. The external text
       is being removed in favor of rendering inside the component. */
    .gratuity-header {
      /* Override default margin-bottom: 16px.
         The multiSurvey variant uses gap: 12px for child spacing,
         so let the gap handle space between subtitle and buttons. */
      margin-bottom: 0;
    }

    .gratuity-description {
      color: #545454;
      font-size: 13px;
      line-height: 18px;
    }
  }
`

const FullyDynamicGratuity = ({
  onTipRateChange,
  onCustomInputModeChange,
  isTipRateAdjustableMarket,
  finalPrice,
  baseTipAmount,
  // Variant system — replaces individual title/subtitle/cashText props
  variant = 'checkout',
  mode = 'percentage',
  currentTipDollarAmount = 0,
  // Callbacks and initial state for survey context
  initialTipInCash = false,
  onTipInCashChange,
  onDollarAmountChange,
  // Number of services this provider performed.
  // Scales dollar-mode presets: $5/$10/$15 → $10/$20/$30 for 2 services.
  // Percentage mode is unaffected (already scales via baseTipAmount).
  serviceCount = 1,
  // Tracking props
  totalPrice,         // checkout: appointment totalPrice
  appointmentId,      // survey: appointment ID
  originalTipRate,    // survey: original tip rate from booking
  isPartialTipper,    // survey single: whether client is a partial tipper
  servicePrice,       // survey: service price for tracking
  providerId,         // survey multi: provider ID
  onAdjustTipClick,   // survey: callback when user taps "Adjust tip"
  hidePresets = false, // survey: when true, hide all preset buttons after adjust-tip save
  // Whether the "I'll tip in cash" checkbox may be shown. Gated by the
  // `hide-tip-in-cash` experiment at checkout; defaults to true (survey unaffected).
  showTipInCash = true,
}) => {
  // Scale dollar presets by service count for consolidated providers.
  // e.g. 2 services → $10, $20, $30 instead of $5, $10, $15
  const scaledDollarOptions = DOLLAR_TIP_OPTIONS.map(opt => ({
    amount: opt.amount * serviceCount,
    label: `$${opt.amount * serviceCount}`,
  }))

  // Resolve variant into concrete display values
  const config = getVariantConfig(variant, { currentTipDollarAmount, hidePresets })

  const isSurvey = variant === 'survey' || variant === 'multiSurvey'
  const isDollarMode = mode === 'dollar'
  // Cash toggle in "Other" custom input:
  // - Checkout: show unless the hide-tip-in-cash experiment suppresses it
  // - Survey percentage mode (0% tippers): show (no "Adjust tip" link, so this is their only cash path)
  // - Survey dollar mode (>0% tippers): hide (they access cash toggle via "Adjust tip" modal)
  const showCashInCustomInput = isSurvey ? !isDollarMode : showTipInCash

  // In checkout: 20% pre-selected. In survey: no default — user must actively choose.
  const [selectedTipRate, setSelectedTipRate] = useState(config.defaultTipRate)
  const [customTipAmount, setCustomTipAmount] = useState(null)
  const [isCustomInputMode, setIsCustomInputMode] = useState(false)
  const [customInputValue, setCustomInputValue] = useState('')
  const [isTipInCash, setIsTipInCash] = useState(initialTipInCash)
  // Dollar mode state — tracks which dollar preset button is selected
  const [selectedDollarAmount, setSelectedDollarAmount] = useState(null)

  // Notify parent when custom input mode opens/closes
  // so the booking button can be disabled while the user
  // is in the middle of entering a custom tip amount
  useEffect(() => {
    onCustomInputModeChange?.(isCustomInputMode)
  }, [isCustomInputMode, onCustomInputModeChange])

  const handleTipSelect = rate => {
    // Survey: tap an already-selected percentage preset to deselect (toggle off).
    // Check customTipAmount === null to match the aria-pressed logic — a button is
    // only "active" when the rate matches AND there's no custom amount.
    if (isSurvey && selectedTipRate === rate && customTipAmount === null) {
      setSelectedTipRate(null)
      setCustomTipAmount(null)
      setSelectedDollarAmount(null)
      setIsCustomInputMode(false)
      onTipRateChange?.(null)
      onDollarAmountChange?.(null)
      return
    }

    if (isSurvey) {
      trackSurveyTipRateSelected({
        appointmentId, selectedTipRate: rate, originalTipRate,
        isPartialTipper, servicePrice, providerId,
        isMultiService: variant === 'multiSurvey',
      })
    } else {
      trackCheckoutTipRateSelected(rate, selectedTipRate, totalPrice)
    }
    setSelectedTipRate(rate)
    setCustomTipAmount(null)
    setSelectedDollarAmount(null)
    setIsTipInCash(false)
    setIsCustomInputMode(false)
    onTipRateChange(rate)
    onDollarAmountChange?.(null)
    // If user taps a rate while cash was checked, notify parent that cash is unchecked
    if (isSurvey && isTipInCash) {
      onTipInCashChange?.(false)
    }
  }

  // Handle preset dollar amount selection (survey dollar mode only).
  // Notifies parent via onDollarAmountChange so SingleSurvey can
  // track the flat dollar amount for the additionalTipAmount mutation.
  const handleDollarSelect = (amount) => {
    // Survey: tap an already-selected dollar preset to deselect (toggle off)
    if (isSurvey && selectedDollarAmount === amount) {
      setSelectedDollarAmount(null)
      setSelectedTipRate(null)
      onDollarAmountChange?.(null)
      onTipRateChange?.(null)
      return
    }

    trackSurveyTipDollarAmountSelected({
      appointmentId, additionalTipDollar: amount, servicePrice,
      providerId, isMultiService: variant === 'multiSurvey',
    })
    setSelectedDollarAmount(amount)
    setSelectedTipRate(null)
    setCustomTipAmount(null)
    setIsTipInCash(false)
    setIsCustomInputMode(false)
    onDollarAmountChange?.(amount)
    // Also fire onTipRateChange with the equivalent rate
    if (baseTipAmount > 0) {
      onTipRateChange?.(amount / baseTipAmount)
    }
    // If user taps a dollar amount while cash was checked, notify parent
    if (isSurvey && isTipInCash) {
      onTipInCashChange?.(false)
    }
  }

  const handleCustomClick = () => {
    // Second tap on "Other" while input is open — cancel
    if (isCustomInputMode) {
      setIsCustomInputMode(false)
      setCustomInputValue('')
      setCustomTipAmount(null)

      // If cash was checked during custom input, cancel means "nevermind" — uncheck it
      if (isTipInCash) {
        setIsTipInCash(false)
        onTipInCashChange?.(false)
      }

      if (isSurvey) {
        // Survey: return to no selection — user must actively choose
        setSelectedTipRate(null)
        setSelectedDollarAmount(null)
        onTipRateChange?.(null)
        onDollarAmountChange?.(null)
      } else {
        // Checkout: reset to 20% default (existing behavior)
        setSelectedTipRate(0.2)
        onTipRateChange(0.2)
      }
      return
    }
    // If user opens custom input while cash was checked, uncheck cash
    if (isTipInCash) {
      setIsTipInCash(false)
      onTipInCashChange?.(false)
    }
    setIsCustomInputMode(true)
    setSelectedTipRate(null)
    setSelectedDollarAmount(null)
    setCustomInputValue(customTipAmount !== null ? String(customTipAmount) : '')
  }

  const maxTipAmount = baseTipAmount * MAX_TIP_RATE

  const handleCustomInputChange = e => {
    const text = e.target.value
    // Allow only numbers and decimal point
    const sanitized = text.replace(/[^0-9.]/g, '')
    // Prevent multiple decimal points
    const parts = sanitized.split('.')
    if (parts.length > 2) {
      return
    }
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return
    }
    setCustomInputValue(sanitized)

    // Typing a custom amount means the user is choosing a digital tip — uncheck cash
    if (isTipInCash) {
      setIsTipInCash(false)
      onTipInCashChange?.(false)
    }
  }

  const customInputExceedsMax =
    customInputValue !== '' &&
    !isNaN(parseFloat(customInputValue)) &&
    parseFloat(customInputValue) > maxTipAmount

  const handleCustomSave = () => {
    const amount = parseFloat(customInputValue) || 0
    setCustomTipAmount(amount)
    setIsCustomInputMode(false)
    setSelectedDollarAmount(null)

    if (isDollarMode) {
      trackSurveyTipDollarAmountSelected({
        appointmentId, additionalTipDollar: amount, servicePrice,
        providerId, isMultiService: variant === 'multiSurvey',
      })
      // In dollar mode, custom amount IS the dollar amount
      onDollarAmountChange?.(amount)
      if (baseTipAmount > 0) {
        onTipRateChange?.(amount / baseTipAmount)
      }
    } else {
      // In percentage mode, convert dollar amount to rate (existing behavior)
      const rate = baseTipAmount > 0 ? amount / baseTipAmount : 0
      if (isSurvey) {
        trackSurveyTipRateSelected({
          appointmentId, selectedTipRate: rate, originalTipRate,
          isPartialTipper, servicePrice, providerId,
          isMultiService: variant === 'multiSurvey',
        })
      } else {
        trackCheckoutTipRateSelected(rate, selectedTipRate, totalPrice)
      }
      setSelectedTipRate(null)
      onTipRateChange(rate)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleCustomSave()
    }
  }

  const handleTipInCashToggle = () => {
    const newTipInCash = !isTipInCash
    // Tracking: fire the appropriate event for the current context
    if (isSurvey) {
      trackSurveyTipInCashToggled({
        appointmentId, tippedInCash: newTipInCash,
        providerId, isMultiService: variant === 'multiSurvey',
      })
    } else {
      trackCheckoutTipInCashToggled(newTipInCash, totalPrice)
    }

    if (!isTipInCash) {
      // --- CHECKING the box ---
      setIsTipInCash(true)
      onTipInCashChange?.(true)

      if (isCustomInputMode) {
        // Checkbox is inside the custom input section — populate the input with $0.
        // Also notify parent of rate = 0 so checkout state stays consistent
        // (even though "Book Now" is disabled during custom input mode).
        setCustomInputValue('0')
        onTipRateChange(0)
        onDollarAmountChange?.(null)
      } else {
        // Checkbox is outside the custom input (survey's current layout) —
        // clear all tip selections immediately and notify parent of rate = 0
        setSelectedTipRate(null)
        setSelectedDollarAmount(null)
        setCustomTipAmount(null)
        onTipRateChange(0)
        onDollarAmountChange?.(null)
      }
    } else {
      // --- UNCHECKING the box ---
      setIsTipInCash(false)
      onTipInCashChange?.(false)

      if (isCustomInputMode) {
        // Checkbox is inside custom input — clear the field so user can type a new amount
        setCustomInputValue('')
      } else if (isSurvey) {
        // Survey (outside custom input): no default — user must actively choose
        setSelectedTipRate(null)
        setSelectedDollarAmount(null)
        onTipRateChange?.(null)
        onDollarAmountChange?.(null)
      } else {
        // Checkout (outside custom input): revert to 20% default
        setSelectedTipRate(0.2)
        setCustomTipAmount(null)
        onTipRateChange(0.2)
      }
    }
  }

  // In checkout mode, hide when market doesn't support adjustable tips or price is $0.
  // In survey mode, the parent (SingleSurvey) handles visibility logic,
  // so if we're rendered at all, we should show.
  if (!isSurvey && (!isTipRateAdjustableMarket || finalPrice === 0)) {
    return null
  }

  return (
    <GratuityContainer data-variant={variant}>
      {config.showTitle && (
        <div className="gratuity-header">
          {/* Only render the heading element if there's title text — multiSurvey
              has an empty title (provider photo/name serves as the header above),
              so we skip the element entirely to avoid an invisible heading in the DOM. */}
          {config.title && (
            <h3 id="gratuity-title" className="gratuity-title">
              {config.title}
            </h3>
          )}
          {config.subtitle && (
            <p className="gratuity-description">
              {config.subtitle}
            </p>
          )}
        </div>
      )}

      {/* Tip preset buttons — hidden after user adjusts tip via modal */}
      {!hidePresets && (
        <div
          role="group"
          aria-labelledby={config.title ? 'gratuity-title' : undefined}
          aria-label={!config.title ? 'Tip options' : undefined}
          className="tip-button-row"
        >
          {isDollarMode ? (
            <>
              {/* Dollar-mode presets: scaled by serviceCount ($5×n, $10×n, $15×n) */}
              {scaledDollarOptions.map(({ amount, label }) => (
                <button
                  key={amount}
                  type="button"
                  aria-pressed={selectedDollarAmount === amount}
                  className={`tip-button ${selectedDollarAmount === amount ? 'active' : ''}`}
                  onClick={() => handleDollarSelect(amount)}
                >
                  <p className="tip-rate-label">{label}</p>
                </button>
              ))}
            </>
          ) : (
            <>
              {/* Percentage-mode presets: 20%, 25%, 30% */}
              {TIP_OPTIONS.map(({ rate, label }) => (
                <button
                  key={rate}
                  type="button"
                  aria-pressed={selectedTipRate === rate && customTipAmount === null}
                  className={`tip-button ${selectedTipRate === rate && customTipAmount === null ? 'active' : ''}`}
                  onClick={() => handleTipSelect(rate)}
                >
                  <p className="tip-rate-label">{label}</p>
                  <p className="tip-amount-label">
                    {formatCurrency(baseTipAmount * rate)}
                  </p>
                </button>
              ))}
            </>
          )}

          {/* 4th button: "Other" or saved custom value */}
          <button
            type="button"
            aria-pressed={isCustomInputMode || customTipAmount !== null}
            className={`tip-button ${isCustomInputMode || customTipAmount !== null ? 'active' : ''}`}
            onClick={handleCustomClick}
          >
            {customTipAmount !== null && !isCustomInputMode ? (
              <>
                <p className="tip-rate-label">
                  {isDollarMode
                    ? formatCurrency(customTipAmount)
                    : baseTipAmount > 0
                      ? `${Math.round((customTipAmount / baseTipAmount) * 100)}%`
                      : '0%'
                  }
                </p>
                {/* In percentage mode, show dollar amount below the percentage */}
                {!isDollarMode && (
                  <p className="tip-amount-label">
                    {formatCurrency(customTipAmount)}
                  </p>
                )}
              </>
            ) : (
              <p className="tip-rate-label">Other</p>
            )}
          </button>
        </div>
      )}

      {/* "Adjust tip" link — survey only, when original tip > 0%.
          Uses originalTipRate (booking-time rate) so the button persists
          even after the user adjusts their tip down. Hidden during custom
          input mode to avoid competing input flows. */}
      {isSurvey && originalTipRate > 0 && !isCustomInputMode && (
        <button
          type="button"
          className="adjust-tip-link"
          onClick={() => onAdjustTipClick?.()}
        >
          Adjust tip
        </button>
      )}

      {/* Custom input section — only reachable via "Other" button (which is hidden when hidePresets) */}
      {isCustomInputMode && !hidePresets && (
        <div className="custom-input-section">
          <div className="custom-input-wrapper">
            {/* Show "$" prefix once the user starts typing */}
            {customInputValue && (
              <span className="custom-dollar-prefix">$</span>
            )}
            <input
              type="text"
              inputMode="decimal"
              className="custom-tip-underline-input"
              value={customInputValue}
              onChange={handleCustomInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Custom amount"
              aria-label="Enter custom tip amount in dollars"
              autoFocus
            />
          </div>
          {customInputExceedsMax && (
            <p className="custom-tip-error">
              Tip cannot exceed {formatCurrency(maxTipAmount)} (100%)
            </p>
          )}
          {/* Cash checkbox inside custom input — hidden for dollar-mode survey
              (>0% tippers use "Adjust tip" modal for cash). Visible for checkout
              (always), percentage-mode survey (0% tippers' only cash path). */}
          {showCashInCustomInput && (
            <label className="tip-in-cash-label">
              <input
                type="checkbox"
                checked={isTipInCash}
                onChange={handleTipInCashToggle}
                className="tip-in-cash-checkbox"
              />
              {config.tipInCashText}
            </label>
          )}
          <button
            type="button"
            className="save-tip-button"
            disabled={
              customInputValue === '' ||
              isNaN(parseFloat(customInputValue)) ||
              parseFloat(customInputValue) < 0 ||
              customInputExceedsMax
            }
            onClick={handleCustomSave}
          >
            Save
          </button>
        </div>
      )}

    </GratuityContainer>
  )
}

FullyDynamicGratuity.propTypes = {
  onTipRateChange: PropTypes.func.isRequired,
  onCustomInputModeChange: PropTypes.func,
  isTipRateAdjustableMarket: PropTypes.bool,
  baseTipAmount: PropTypes.number,
  finalPrice: PropTypes.number,
  // Variant system
  variant: PropTypes.oneOf(['checkout', 'survey', 'multiSurvey']),
  mode: PropTypes.oneOf(['percentage', 'dollar']),
  currentTipDollarAmount: PropTypes.number,
  // Survey callbacks and initial state
  initialTipInCash: PropTypes.bool,
  onTipInCashChange: PropTypes.func,
  onDollarAmountChange: PropTypes.func,
  // Per-provider consolidation: scales dollar presets
  serviceCount: PropTypes.number,
  // Tracking props
  totalPrice: PropTypes.number,
  appointmentId: PropTypes.string,
  originalTipRate: PropTypes.number,
  isPartialTipper: PropTypes.bool,
  servicePrice: PropTypes.number,
  providerId: PropTypes.string,
  onAdjustTipClick: PropTypes.func,
  hidePresets: PropTypes.bool,
  showTipInCash: PropTypes.bool,
}

export default FullyDynamicGratuity
